import { LightAction } from 'components/ui/LightAction';
import { UsersGroupIcon } from 'components/icons/customIcons';
import { AddIcon } from 'components/icons/Icons';
import { Sizes } from 'components/forms/NumberSelector';
import { CardsSection } from 'components/layout/Section/CardsSection';
import { NsKey } from 'enums/NsKey';
import {
  useBorrowerRiskData
} from 'features/subscription-process/contexts/selectors/selectors';
import { useSubscriptionProcessActions } from 'features/subscription-process/contexts/SubscriptionProcessContext';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { usePanel } from 'hooks/usePanel';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { borrowersAPI } from 'services/swagger-api';
import { MinimalBorrowerForm } from '../../forms/borrower-minimal';
import { BorrowerInsuredCard } from './BorrowerInsuredCard';
import { BorrowerMinimalPersonDto } from 'api/borrower';
import { withPersonName } from 'shared/utils/panelTitle';

export const BorrowerProfilingInsuredSection = () => {
  const { openPanel } = usePanel()
  const { t } = useDynamicTranslation([NsKey.Common, NsKey.Borrower])

  // Source de vérité du projet courant = l'URL (et non un id global potentiellement périmé),
  // sinon une suppression d'assuré pourrait viser le MAUVAIS projet.
  const { borrowerProjectId } = useParams<{ borrowerProjectId: string }>()
  const riskData = useBorrowerRiskData()
  const insureds = riskData?.insureds

  const isInsuredsEmpty = useMemo(() => {
    if (insureds) {
      return insureds.length === 0
    }
    return true
  }, [insureds])

  const { refreshSubscriptionProcess } = useSubscriptionProcessActions()

  // Get IDs of all existing insureds for exclusion from search
  const allInsuredIds = useMemo(() => {
    return insureds?.map((insured: BorrowerMinimalPersonDto) => insured.id).filter((id): id is string => !!id) || [];
  }, [insureds]);

  const onCreateInsuredEntity = useCallback(() => {
    openPanel({
      title: t("COMMON.LABELS.ADD_INSURED_PERSON"),
      size: Sizes["2xl"],
    },
      <MinimalBorrowerForm
        onSuccess={refreshSubscriptionProcess}
        excludeIds={allInsuredIds}
        borrowerProjectId={borrowerProjectId}
      />
    )
  }, [openPanel, t, refreshSubscriptionProcess, allInsuredIds, borrowerProjectId])

  const onUpdatePerson = useCallback((personId: string) => {
    // Exclude all insureds except the one being edited
    const excludeIds = allInsuredIds.filter(id => id !== personId);
    const person = insureds?.find((p: BorrowerMinimalPersonDto) => p.id === personId);
    const personName = person
      ? person.type === 'physique'
        ? `${person.firstName} ${person.lastName}`
        : person.companyName
      : undefined;
    openPanel({
      title: withPersonName(t("COMMON.LABELS.EDIT_INSURED_PERSON"), personName),
      size: Sizes["2xl"],
    },
      <MinimalBorrowerForm
        onSuccess={refreshSubscriptionProcess}
        personId={personId}
        excludeIds={excludeIds}
        borrowerProjectId={borrowerProjectId}
      />
    )
  }, [openPanel, t, refreshSubscriptionProcess, allInsuredIds, borrowerProjectId, insureds])

  const onDeletePerson = useCallback(async (personId: string) => {
    if (!borrowerProjectId) return;

    const person = insureds?.find((p: BorrowerMinimalPersonDto) => p.id === personId);
    if (!person?.borrowerId) return;

    try {
      await borrowersAPI.deleteBorrower({
        borrowerProjectId,
        borrowerId: person.borrowerId
      });
      refreshSubscriptionProcess();
    } catch (error: any) {
      console.error('Error deleting borrower:', error);
      toast.error(
        error?.response?.error?.message || 'Erreur lors de la suppression'
      );
    }
  }, [borrowerProjectId, insureds, refreshSubscriptionProcess]);

  return <CardsSection
    icon={<UsersGroupIcon className="h-8 w-8 text-primary" />}
    title={t("BORROWER.LABELS.PROJECT_INSURED_DESCRIPTION")}
    titleAside={!isInsuredsEmpty && <LightAction icon={AddIcon} label={t("COMMON.LABELS.ADD_INSURED_PERSON")} onClick={() => onCreateInsuredEntity()} />}
  >
    <div className="flex items-stretch gap-4">
      {
        isInsuredsEmpty &&
        <div className="flex items-center justify-center bg-white w-full rounded-xl border p-8">
          <LightAction icon={AddIcon} label={t("COMMON.LABELS.ADD_INSURED_PERSON")} onClick={() => onCreateInsuredEntity()} />
        </div>
      }
      {
        !isInsuredsEmpty &&
        <div className="grid grid-cols-2 gap-4 w-full">
          {
            insureds?.map((entity: BorrowerMinimalPersonDto, index: number) => (
              <BorrowerInsuredCard
                key={index}
                person={entity}
                onUpdate={onUpdatePerson}
                onDelete={onDeletePerson}
              />
            ))
          }
        </div>
      }
    </div>
  </CardsSection>
}
